import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthFetch } from '../hooks/useAuthFetch';
import ConfirmDialog from '../components/ConfirmDialog';
import { useIsMobile } from '../hooks/useIsMobile';

// ─── Constantes ───────────────────────────────────────────────────────────────
const DAYS_FR    = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS_FR  = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const EVENT_TYPES = [
  { value: 'rdv',     label: 'Rendez-vous', color: '#41B073' },
  { value: 'appel',   label: 'Appel',       color: '#3b82f6' },
  { value: 'relance', label: 'Relance',     color: '#f59e0b' },
  { value: 'interne', label: 'Interne',     color: '#22c55e' },
  { value: 'google',  label: 'Google',      color: '#4285F4' },
  { value: 'autre',   label: 'Autre',       color: '#94a3b8' },
];
const PRIORITY_LABELS = { haute: '🔴', normale: '🟡', basse: '🟢' };

// ─── Helpers ──────────────────────────────────────────────────────────────────
// isoDate : retourne YYYY-MM-DD en heure locale (getFullYear/getMonth/getDate)
// pour éviter le décalage UTC qui fait apparaître un jour en arrière en UTC+x.
function isoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Convertit un ISO UTC en chaîne "YYYY-MM-DDTHH:mm" pour un datetime-local input.
// Le navigateur affiche l'heure locale ; new Date(isoStr) fait la conversion UTC→local.
function toLocalInput(isoStr) {
  if (!isoStr) return '';
  const d  = new Date(isoStr);
  const y  = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  const h  = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${mo}-${da}T${h}:${mi}`;
}

// Convertit la valeur d'un datetime-local input (heure locale "YYYY-MM-DDTHH:mm")
// en ISO UTC. new Date(localStr) est traité comme heure locale par les navigateurs modernes.
function fromLocalInput(localStr) {
  if (!localStr) return '';
  return new Date(localStr).toISOString();
}

// Extrait la date locale (YYYY-MM-DD) depuis une chaîne ISO UTC ou un string de date.
function localDateStr(isoStr) {
  if (!isoStr) return '';
  return isoDate(new Date(isoStr));
}

function fmtDate(str) { return str ? new Date(str).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '—'; }
function fmtTime(str) {
  if (!str) return '';
  return new Date(str).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Retourne la grille de jours du mois (6 semaines, commence lundi).
 * Les cases hors-mois ont { outOfMonth: true }.
 */
function buildMonthGrid(year, month) {
  const firstDay    = new Date(year, month, 1);
  // Lundi = 0 dans notre grille (js: lundi=1 → offset 0, dimanche=0 → offset 6)
  const startOffset = (firstDay.getDay() + 6) % 7;

  const grid = [];
  let   day  = new Date(firstDay);
  day.setDate(day.getDate() - startOffset);

  for (let week = 0; week < 6; week++) {
    const row = [];
    for (let d = 0; d < 7; d++) {
      row.push({
        date:       new Date(day),
        dateStr:    isoDate(day),
        outOfMonth: day.getMonth() !== month,
      });
      day.setDate(day.getDate() + 1);
    }
    grid.push(row);
  }
  return grid;
}

// ─── Composant Modal Événement ────────────────────────────────────────────────
function EventModal({ event, clients, onSave, onDelete, onClose }) {
  const isNew    = !event.id;
  const [form, setForm] = useState({
    title:            event.title            || '',
    description:      event.description      || '',
    start:            event.start            || '',
    end:              event.end              || '',
    all_day:          event.all_day          || false,
    type:             event.type             || 'rdv',
    color:            event.color            || '#41B073',
    client_id:        event.client_id        || '',
    location:         event.location         || '',
    reminder_minutes: event.reminder_minutes != null ? event.reminder_minutes : 30,
    attendees:        Array.isArray(event.attendees) ? event.attendees : [],
  });
  const [attendeeInput, setAttendeeInput] = useState('');

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  function addAttendee() {
    const email = attendeeInput.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (form.attendees.includes(email)) { setAttendeeInput(''); return; }
    set('attendees', [...form.attendees, email]);
    setAttendeeInput('');
  }

  function removeAttendee(email) {
    set('attendees', form.attendees.filter(a => a !== email));
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.title || !form.start) return;
    onSave(form);
  }

  // Mettre à jour la couleur automatiquement selon le type choisi
  function handleTypeChange(val) {
    const t = EVENT_TYPES.find(t => t.value === val);
    setForm(f => ({ ...f, type: val, color: t?.color || f.color }));
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }} onClick={onClose}>
      <div style={{
        background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 12,
        padding: 28, width: 480, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ margin: 0, color: '#e2e8f0', fontSize: 16 }}>
            {isNew ? 'Nouvel événement' : 'Modifier l\'événement'}
          </h3>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer', fontSize:20 }}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Titre */}
            <div>
              <label style={labelStyle}>Titre *</label>
              <input
                value={form.title} onChange={e => set('title', e.target.value)}
                required autoFocus style={inputStyle} placeholder="Titre de l'événement"
              />
            </div>

            {/* Type */}
            <div>
              <label style={labelStyle}>Type</label>
              <select value={form.type} onChange={e => handleTypeChange(e.target.value)} style={inputStyle}>
                {EVENT_TYPES.filter(t => t.value !== 'google').map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            {/* Journée entière */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94a3b8', fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.all_day} onChange={e => set('all_day', e.target.checked)} />
              Journée entière
            </label>

            {/* Dates */}
            <div className="form-grid" style={{ gap: 10 }}>
              <div>
                <label style={labelStyle}>Début *</label>
                <input
                  type={form.all_day ? 'date' : 'datetime-local'}
                  value={form.all_day ? (form.start?.split('T')[0] || '') : toLocalInput(form.start)}
                  onChange={e => set('start', form.all_day ? e.target.value : fromLocalInput(e.target.value))}
                  required style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Fin</label>
                <input
                  type={form.all_day ? 'date' : 'datetime-local'}
                  value={form.all_day ? (form.end?.split('T')[0] || '') : toLocalInput(form.end)}
                  onChange={e => set('end', form.all_day ? e.target.value : fromLocalInput(e.target.value))}
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Lieu */}
            <div>
              <label style={labelStyle}>📍 Lieu</label>
              <input
                value={form.location}
                onChange={e => set('location', e.target.value)}
                style={inputStyle}
                placeholder="Adresse, salle de réunion, lien visio..."
              />
            </div>

            {/* Rappel + Client lié sur la même ligne */}
            <div className="form-grid" style={{ gap: 10 }}>
              <div>
                <label style={labelStyle}>🔔 Rappel</label>
                <select value={form.reminder_minutes} onChange={e => set('reminder_minutes', e.target.value === '' ? null : Number(e.target.value))} style={inputStyle}>
                  <option value="">Aucun</option>
                  <option value={5}>5 minutes avant</option>
                  <option value={10}>10 minutes avant</option>
                  <option value={15}>15 minutes avant</option>
                  <option value={30}>30 minutes avant</option>
                  <option value={60}>1 heure avant</option>
                  <option value={120}>2 heures avant</option>
                  <option value={1440}>1 jour avant</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Client lié</label>
                <select value={form.client_id} onChange={e => set('client_id', e.target.value)} style={inputStyle}>
                  <option value="">— Aucun —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
              </div>
            </div>

            {/* Invités */}
            <div>
              <label style={labelStyle}>👥 Invités</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  type="email"
                  value={attendeeInput}
                  onChange={e => setAttendeeInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAttendee(); } }}
                  placeholder="adresse@email.com"
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button
                  type="button"
                  onClick={addAttendee}
                  style={{ padding: '8px 14px', borderRadius: 6, background: '#41B07320', border: '1px solid #41B07340', color: '#41B073', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}
                >
                  + Ajouter
                </button>
              </div>
              {form.attendees.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {form.attendees.map(email => (
                    <span key={email} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#41B07315', border: '1px solid #41B07330', borderRadius: 20, padding: '3px 10px', fontSize: 12, color: '#a5b4fc' }}>
                      {email}
                      <button type="button" onClick={() => removeAttendee(email)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <label style={labelStyle}>Description</label>
              <textarea
                value={form.description}
                onChange={e => set('description', e.target.value)}
                style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }}
                placeholder="Notes, ordre du jour, lien de réunion..."
              />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
              {!isNew && (
                <button type="button" onClick={() => onDelete(event.id)}
                  style={{ ...btnStyle, background: '#ef444420', color: '#ef4444', borderColor: '#ef444440' }}>
                  Supprimer
                </button>
              )}
              <button type="button" onClick={onClose} style={{ ...btnStyle, background: '#ffffff10', color: '#94a3b8' }}>
                Annuler
              </button>
              <button type="submit" style={{ ...btnStyle, background: '#41B073', color: '#fff', border: 'none' }}>
                {isNew ? 'Créer' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Composant Modal Tâche ────────────────────────────────────────────────────
function TaskModal({ task, clients, onSave, onDelete, onClose }) {
  const isNew  = !task.id;
  const [form, setForm] = useState({
    title:       task.title       || '',
    description: task.description || '',
    due_date:    task.due_date    || '',
    priority:    task.priority    || 'normale',
    client_id:   task.client_id   || '',
  });
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }} onClick={onClose}>
      <div style={{
        background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 12,
        padding: 28, width: 420, maxWidth: '95vw',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ margin: 0, color: '#e2e8f0', fontSize: 16 }}>{isNew ? 'Nouvelle tâche' : 'Modifier la tâche'}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer', fontSize:20 }}>×</button>
        </div>
        <form onSubmit={e => { e.preventDefault(); if (form.title) onSave(form); }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={labelStyle}>Titre *</label>
              <input value={form.title} onChange={e => set('title', e.target.value)} required autoFocus style={inputStyle} />
            </div>
            <div className="form-grid" style={{ gap: 10 }}>
              <div>
                <label style={labelStyle}>Échéance</label>
                <input type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Priorité</label>
                <select value={form.priority} onChange={e => set('priority', e.target.value)} style={inputStyle}>
                  <option value="haute">🔴 Haute</option>
                  <option value="normale">🟡 Normale</option>
                  <option value="basse">🟢 Basse</option>
                </select>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Client lié (optionnel)</label>
              <select value={form.client_id} onChange={e => set('client_id', e.target.value)} style={inputStyle}>
                <option value="">— Aucun —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Description</label>
              <textarea value={form.description} onChange={e => set('description', e.target.value)}
                style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              {!isNew && (
                <button type="button" onClick={() => onDelete(task.id)}
                  style={{ ...btnStyle, background: '#ef444420', color: '#ef4444', borderColor: '#ef444440' }}>
                  Supprimer
                </button>
              )}
              <button type="button" onClick={onClose} style={{ ...btnStyle, background: '#ffffff10', color: '#94a3b8' }}>Annuler</button>
              <button type="submit" style={{ ...btnStyle, background: '#41B073', color: '#fff', border: 'none' }}>
                {isNew ? 'Créer' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Styles partagés ──────────────────────────────────────────────────────────
const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  background: '#0f1117', border: '1px solid #2a2d3e', borderRadius: 6,
  color: '#e2e8f0', padding: '8px 10px', fontSize: 13,
};
const labelStyle = { display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4, fontWeight: 500 };
const btnStyle   = { padding: '8px 16px', borderRadius: 6, border: '1px solid #2a2d3e', cursor: 'pointer', fontSize: 13, fontWeight: 500 };

// ─── Page principale ──────────────────────────────────────────────────────────
export default function AgendaPage() {
  const fetchWithAuth  = useAuthFetch();
  const [searchParams] = useSearchParams();

  const today     = new Date();
  const isMobile  = useIsMobile();
  const [viewDate, setViewDate] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [view, setView]         = useState(() => window.innerWidth < 768 ? 'list' : 'month'); // 'month' | 'list'

  const [events, setEvents]     = useState([]);
  const [tasks, setTasks]       = useState([]);
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(true);

  const [modalEvent, setModalEvent] = useState(null); // null | { ...event } | {} (new)
  const [modalTask,  setModalTask]  = useState(null);

  const [googleConnected, setGoogleConnected] = useState(false);
  const [syncing, setSyncing]                 = useState(false);
  const [confirmDel, setConfirmDel]           = useState(null); // { type: 'event'|'task', id, label }

  // Feedback connexion Google depuis query param
  const googleStatus = searchParams.get('google');

  // ─── Chargement initial ─────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      fetchWithAuth('/api/agenda').then(r => r.json()),
      fetchWithAuth('/api/tasks').then(r => r.json()),
      fetchWithAuth('/api/clients?limit=200').then(r => r.json()),
      fetchWithAuth('/api/google/status').then(r => r.json()),
    ]).then(([evts, tks, cls, gStatus]) => {
      setEvents(Array.isArray(evts) ? evts : []);
      setTasks(Array.isArray(tks) ? tks : []);
      setClients(cls.data || []);
      setGoogleConnected(gStatus.connected || false);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // ─── Recharger les événements ───────────────────────────────────────────────
  const reloadEvents = useCallback(async () => {
    const res  = await fetchWithAuth('/api/agenda');
    const data = await res.json();
    setEvents(Array.isArray(data) ? data : []);
  }, [fetchWithAuth]);

  const reloadTasks = useCallback(async () => {
    const res  = await fetchWithAuth('/api/tasks');
    const data = await res.json();
    setTasks(Array.isArray(data) ? data : []);
  }, [fetchWithAuth]);

  // ─── CRUD Événements ────────────────────────────────────────────────────────
  async function saveEvent(form) {
    const isNew = !modalEvent.id;
    const url   = isNew ? '/api/agenda' : `/api/agenda/${modalEvent.id}`;
    await fetchWithAuth(url, {
      method:  isNew ? 'POST' : 'PUT',
      body:    JSON.stringify(form),
    });
    setModalEvent(null);
    await reloadEvents();
  }

  async function deleteEvent(id) {
    const ev = events.find(e => e.id === id);
    setConfirmDel({ type: 'event', id, label: ev?.title || 'cet événement' });
  }

  async function _doDeleteEvent(id) {
    await fetchWithAuth(`/api/agenda/${id}`, { method: 'DELETE' });
    setModalEvent(null);
    setConfirmDel(null);
    await reloadEvents();
  }

  // ─── CRUD Tâches ────────────────────────────────────────────────────────────
  async function saveTask(form) {
    const isNew = !modalTask.id;
    const url   = isNew ? '/api/tasks' : `/api/tasks/${modalTask.id}`;
    await fetchWithAuth(url, {
      method:  isNew ? 'POST' : 'PUT',
      body:    JSON.stringify(form),
    });
    setModalTask(null);
    await reloadTasks();
  }

  async function deleteTask(id) {
    const t = tasks.find(t => t.id === id);
    setConfirmDel({ type: 'task', id, label: t?.title || 'cette tâche' });
  }

  async function _doDeleteTask(id) {
    await fetchWithAuth(`/api/tasks/${id}`, { method: 'DELETE' });
    setModalTask(null);
    setConfirmDel(null);
    await reloadTasks();
  }

  async function toggleTask(id) {
    await fetchWithAuth(`/api/tasks/${id}/done`, { method: 'PATCH' });
    await reloadTasks();
  }

  // ─── Google sync manuel ─────────────────────────────────────────────────────
  async function handleManualSync() {
    setSyncing(true);
    try {
      await fetchWithAuth('/api/agenda/sync', { method: 'POST' });
      await reloadEvents();
    } finally {
      setSyncing(false);
    }
  }

  // ─── Calendrier mois ────────────────────────────────────────────────────────
  const grid = buildMonthGrid(viewDate.year, viewDate.month);

  function eventsOnDay(dateStr) {
    return events.filter(e => {
      const s  = localDateStr(e.start);
      const en = localDateStr(e.end) || s;
      return s <= dateStr && dateStr <= en;
    });
  }

  function tasksOnDay(dateStr) {
    return tasks.filter(t => t.statut === 'pending' && t.due_date === dateStr);
  }

  function prevMonth() {
    setViewDate(v => {
      const m = v.month === 0 ? 11 : v.month - 1;
      const y = v.month === 0 ? v.year - 1 : v.year;
      return { year: y, month: m };
    });
  }

  function nextMonth() {
    setViewDate(v => {
      const m = v.month === 11 ? 0 : v.month + 1;
      const y = v.month === 11 ? v.year + 1 : v.year;
      return { year: y, month: m };
    });
  }

  // ─── Événements + tâches à venir (vue liste) ────────────────────────────────
  const todayStr = isoDate(today);

  const upcomingEvents = [...events]
    .filter(e => localDateStr(e.start) >= todayStr)
    .sort((a, b) => a.start.localeCompare(b.start))
    .slice(0, 20);

  // Tâches pending avec une date d'échéance — affichées dans la grille et la liste
  const upcomingTasks = tasks
    .filter(t => t.statut === 'pending' && t.due_date)
    .sort((a, b) => a.due_date.localeCompare(b.due_date));

  // Items fusionnés pour la vue liste (événements + tâches avec date), triés par date
  const listItems = [
    ...upcomingEvents.map(e => ({ _kind: 'event', _date: localDateStr(e.start), ...e })),
    ...upcomingTasks.filter(t => t.due_date >= todayStr).map(t => ({ _kind: 'task', _date: t.due_date, ...t })),
  ].sort((a, b) => a._date.localeCompare(b._date)).slice(0, 30);

  // ─── Tâches en cours ────────────────────────────────────────────────────────
  const pendingTasks = tasks.filter(t => t.statut === 'pending');
  const overdueTasks = pendingTasks.filter(t => t.is_overdue);

  if (loading) return <div style={{ padding: 40, color: '#94a3b8' }}>Chargement...</div>;

  return (
    <div style={{ padding: 28, maxWidth: 1400, margin: '0 auto' }}>

      {/* ── Bandeau connexion Google ── */}
      {googleStatus === 'connected' && (
        <div style={{ background: '#22c55e20', border: '1px solid #22c55e40', borderRadius: 8, padding: '10px 16px', marginBottom: 16, color: '#22c55e', fontSize: 13 }}>
          ✅ Compte Google connecté — vos événements Google Calendar seront synchronisés automatiquement.
        </div>
      )}
      {googleStatus === 'error' && (
        <div style={{ background: '#ef444420', border: '1px solid #ef444440', borderRadius: 8, padding: '10px 16px', marginBottom: 16, color: '#ef4444', fontSize: 13 }}>
          ❌ Erreur lors de la connexion Google. Réessayez depuis le bouton ci-dessous.
        </div>
      )}

      {/* ── En-tête ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>Agenda</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>
            {today.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Google Calendar */}
          {googleConnected ? (
            <button onClick={handleManualSync} disabled={syncing}
              style={{ ...topBtnStyle, color: '#4285F4', borderColor: '#4285F440' }}>
              {syncing ? '⟳ Sync...' : '⟳ Sync Google'}
            </button>
          ) : (
            <button
              onClick={async () => {
                try {
                  // fetchWithAuth envoie le JWT — verifyToken passe correctement
                  const res  = await fetchWithAuth('/api/google/auth');
                  const data = await res.json();
                  if (data.url) {
                    // Rediriger le navigateur vers Google OAuth
                    window.location.href = data.url;
                  } else {
                    alert(data.error || 'Impossible de générer l\'URL Google');
                  }
                } catch {
                  alert('Erreur lors de la connexion à Google');
                }
              }}
              style={{ ...topBtnStyle, color: '#4285F4', borderColor: '#4285F440', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Connecter Google Calendar
            </button>
          )}
          <button onClick={() => setModalTask({})} style={{ ...topBtnStyle, color: '#94a3b8' }}>
            + Tâche
          </button>
          <button onClick={() => setModalEvent({ start: fromLocalInput(todayStr + 'T09:00'), end: fromLocalInput(todayStr + 'T10:00') })}
            style={{ ...topBtnStyle, background: '#41B073', color: '#fff', border: 'none' }}>
            + Événement
          </button>
        </div>
      </div>

      {/* ── Contenu principal ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 300px', gap: 20, alignItems: 'start' }}>

        {/* ── Calendrier ── */}
        <div style={{ background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 10, overflow: 'hidden' }}>
          {/* Nav mois */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #2a2d3e' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <button onClick={prevMonth} style={navBtnStyle}>‹</button>
              <span style={{ fontWeight: 700, fontSize: 16, color: '#e2e8f0', minWidth: 160, textAlign: 'center' }}>
                {MONTHS_FR[viewDate.month]} {viewDate.year}
              </span>
              <button onClick={nextMonth} style={navBtnStyle}>›</button>
              <button onClick={() => setViewDate({ year: today.getFullYear(), month: today.getMonth() })}
                style={{ ...navBtnStyle, fontSize: 11, padding: '3px 10px', color: '#41B073' }}>
                Aujourd'hui
              </button>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {(isMobile ? ['list'] : ['month', 'list']).map(v => (
                <button key={v} onClick={() => setView(v)}
                  style={{ ...navBtnStyle, background: view === v ? '#41B07320' : 'transparent', color: view === v ? '#41B073' : '#64748b', borderColor: view === v ? '#41B07340' : '#2a2d3e' }}>
                  {v === 'month' ? 'Mois' : 'Liste'}
                </button>
              ))}
            </div>
          </div>

          {/* Vue mois */}
          {view === 'month' && (
            <div>
              {/* Jours de la semaine */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', borderBottom: '1px solid #2a2d3e' }}>
                {DAYS_FR.map(d => (
                  <div key={d} style={{ padding: '8px 0', textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>{d}</div>
                ))}
              </div>
              {/* Grille */}
              {grid.map((week, wi) => (
                <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', borderBottom: wi < 5 ? '1px solid #2a2d3e' : 'none' }}>
                  {week.map((cell, di) => {
                    const isToday   = cell.dateStr === todayStr;
                    const dayEvents = eventsOnDay(cell.dateStr);
                    const dayTasks  = tasksOnDay(cell.dateStr);
                    const totalItems = dayEvents.length + dayTasks.length;
                    const slots = 3; // max affiché avant "+N autres"
                    let shown = 0;
                    return (
                      <div
                        key={di}
                        onClick={() => setModalEvent({ start: fromLocalInput(cell.dateStr + 'T09:00'), end: fromLocalInput(cell.dateStr + 'T10:00') })}
                        style={{
                          minHeight:   90,
                          padding:     '6px 6px 4px',
                          borderRight: di < 6 ? '1px solid #2a2d3e' : 'none',
                          background:  cell.outOfMonth ? '#12141f' : 'transparent',
                          cursor:      'pointer',
                          transition:  'background 0.1s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = cell.outOfMonth ? '#16192a' : '#ffffff05'}
                        onMouseLeave={e => e.currentTarget.style.background = cell.outOfMonth ? '#12141f' : 'transparent'}
                      >
                        {/* Numéro du jour */}
                        <div style={{
                          width:      22, height: 22,
                          display:    'flex', alignItems: 'center', justifyContent: 'center',
                          borderRadius: '50%',
                          background:  isToday ? '#41B073' : 'transparent',
                          color:       isToday ? '#fff' : cell.outOfMonth ? '#3a3d52' : '#e2e8f0',
                          fontSize:    12, fontWeight: isToday ? 700 : 400,
                          marginBottom: 3,
                        }}>{cell.date.getDate()}</div>

                        {/* Événements du jour */}
                        {dayEvents.map(ev => {
                          if (shown >= slots) return null;
                          shown++;
                          return (
                            <div
                              key={ev.id}
                              onClick={e => { e.stopPropagation(); setModalEvent(ev); }}
                              title={ev.title}
                              style={{
                                background:   (ev.color || '#41B073') + '30',
                                borderLeft:   `2px solid ${ev.color || '#41B073'}`,
                                color:        ev.color || '#41B073',
                                fontSize:     10,
                                padding:      '1px 4px',
                                borderRadius: '0 3px 3px 0',
                                marginBottom: 2,
                                whiteSpace:   'nowrap',
                                overflow:     'hidden',
                                textOverflow: 'ellipsis',
                                cursor:       'pointer',
                              }}
                            >
                              {!ev.all_day && <span style={{ opacity: 0.8 }}>{fmtTime(ev.start)} </span>}
                              {ev.title}
                            </div>
                          );
                        })}

                        {/* Tâches du jour */}
                        {dayTasks.map(t => {
                          if (shown >= slots) return null;
                          shown++;
                          const taskColor = t.is_overdue ? '#ef4444' : { haute: '#ef4444', normale: '#f59e0b', basse: '#22c55e' }[t.priority] || '#f59e0b';
                          return (
                            <div
                              key={'t' + t.id}
                              onClick={e => { e.stopPropagation(); setModalTask(t); }}
                              title={t.title}
                              style={{
                                background:   taskColor + '20',
                                borderLeft:   `2px dashed ${taskColor}`,
                                color:        taskColor,
                                fontSize:     10,
                                padding:      '1px 4px',
                                borderRadius: '0 3px 3px 0',
                                marginBottom: 2,
                                whiteSpace:   'nowrap',
                                overflow:     'hidden',
                                textOverflow: 'ellipsis',
                                cursor:       'pointer',
                              }}
                            >
                              ✓ {t.title}
                            </div>
                          );
                        })}

                        {totalItems > slots && (
                          <div style={{ fontSize: 10, color: '#64748b', paddingLeft: 4 }}>
                            +{totalItems - slots} autre{totalItems - slots > 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* Vue liste */}
          {view === 'list' && (
            <div style={{ padding: 16 }}>
              {listItems.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#64748b', padding: 32, fontSize: 13 }}>
                  Aucun événement ni tâche à venir
                </div>
              ) : (
                listItems.map(item => {
                  if (item._kind === 'task') {
                    const taskColor = item.is_overdue ? '#ef4444' : { haute: '#ef4444', normale: '#f59e0b', basse: '#22c55e' }[item.priority] || '#f59e0b';
                    return (
                      <div
                        key={'task-' + item.id}
                        onClick={() => setModalTask(item)}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 12,
                          padding: '10px 12px', marginBottom: 8,
                          background: '#12141f', border: '1px solid #2a2d3e',
                          borderLeft: `3px dashed ${taskColor}`,
                          borderRadius: '0 8px 8px 0', cursor: 'pointer',
                        }}
                      >
                        <div style={{ minWidth: 60, fontSize: 11, color: '#64748b', textAlign: 'right' }}>
                          <div>{fmtDate(item.due_date)}</div>
                          <div style={{ color: taskColor, fontWeight: 500, fontSize: 10 }}>Échéance</div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 500 }}>
                            {PRIORITY_LABELS[item.priority]} {item.title}
                          </div>
                          {item.client_nom && <div style={{ color: '#64748b', fontSize: 12 }}>👥 {item.client_nom}</div>}
                          {item.description && <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>{item.description}</div>}
                        </div>
                        <span style={{ background: taskColor + '20', color: taskColor, fontSize: 10, padding: '2px 6px', borderRadius: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          Tâche
                        </span>
                      </div>
                    );
                  }
                  // Événement
                  return (
                    <div
                      key={'event-' + item.id}
                      onClick={() => setModalEvent(item)}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12,
                        padding: '10px 12px', marginBottom: 8,
                        background: '#12141f', border: '1px solid #2a2d3e',
                        borderLeft: `3px solid ${item.color || '#41B073'}`,
                        borderRadius: '0 8px 8px 0', cursor: 'pointer',
                      }}
                    >
                      <div style={{ minWidth: 60, fontSize: 11, color: '#64748b', textAlign: 'right' }}>
                        <div>{fmtDate(item.start)}</div>
                        {!item.all_day && <div style={{ color: '#94a3b8', fontWeight: 500 }}>{fmtTime(item.start)}</div>}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 500 }}>{item.title}</div>
                        {item.client_nom && <div style={{ color: '#64748b', fontSize: 12 }}>👥 {item.client_nom}</div>}
                        {item.location && <div style={{ color: '#64748b', fontSize: 12 }}>📍 {item.location}</div>}
                        {item.description && <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>{item.description}</div>}
                      </div>
                      <span style={{ background: (item.color || '#41B073') + '20', color: item.color || '#41B073', fontSize: 10, padding: '2px 6px', borderRadius: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {EVENT_TYPES.find(t => t.value === item.type)?.label || item.type}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* ── Panneau latéral : tâches ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Statistiques rapides */}
          <div className="form-grid" style={{ gap: 8 }}>
            {[
              { label: 'Événements', value: events.length, color: '#41B073' },
              { label: 'Tâches en attente', value: pendingTasks.length, color: '#f59e0b' },
              { label: 'En retard', value: overdueTasks.length, color: '#ef4444' },
              { label: 'Aujourd\'hui', value: eventsOnDay(todayStr).length, color: '#22c55e' },
            ].map(s => (
              <div key={s.label} style={{ background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Tâches */}
          <div style={{ background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #2a2d3e' }}>
              <span style={{ fontWeight: 600, fontSize: 13, color: '#e2e8f0' }}>
                Tâches {pendingTasks.length > 0 && <span style={{ color: '#f59e0b' }}>({pendingTasks.length})</span>}
              </span>
              <button onClick={() => setModalTask({})}
                style={{ background: 'none', border: 'none', color: '#41B073', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>
                +
              </button>
            </div>

            <div style={{ maxHeight: 380, overflowY: 'auto' }}>
              {tasks.length === 0 ? (
                <div style={{ padding: '20px 16px', textAlign: 'center', color: '#64748b', fontSize: 13 }}>
                  Aucune tâche
                </div>
              ) : (
                tasks.slice(0, 15).map(t => (
                  <div key={t.id} style={{
                    display:      'flex',
                    alignItems:   'flex-start',
                    gap:          10,
                    padding:      '10px 16px',
                    borderBottom: '1px solid #1e2235',
                    opacity:      t.statut === 'done' ? 0.5 : 1,
                  }}>
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleTask(t.id)}
                      style={{
                        width: 16, height: 16, borderRadius: 4, flexShrink: 0, marginTop: 2,
                        background:  t.statut === 'done' ? '#22c55e' : 'transparent',
                        border:      `2px solid ${t.statut === 'done' ? '#22c55e' : '#3a3d52'}`,
                        cursor:      'pointer', color: '#fff', fontSize: 10,
                        display:     'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >{t.statut === 'done' ? '✓' : ''}</button>

                    {/* Contenu */}
                    <div style={{ flex: 1, minWidth: 0 }} onClick={() => setModalTask(t)}>
                      <div style={{
                        fontSize:        12, color: '#e2e8f0', cursor: 'pointer',
                        textDecoration:  t.statut === 'done' ? 'line-through' : 'none',
                        whiteSpace:      'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {PRIORITY_LABELS[t.priority] || '🟡'} {t.title}
                      </div>
                      {t.due_date && (
                        <div style={{ fontSize: 10, color: t.is_overdue ? '#ef4444' : '#64748b', marginTop: 2 }}>
                          {t.is_overdue ? '⚠ En retard — ' : ''}{fmtDate(t.due_date)}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {tasks.length > 15 && (
              <div style={{ padding: '8px 16px', borderTop: '1px solid #2a2d3e', textAlign: 'center', color: '#64748b', fontSize: 12 }}>
                +{tasks.length - 15} tâche{tasks.length - 15 > 1 ? 's' : ''} masquée{tasks.length - 15 > 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {modalEvent && (
        <EventModal
          event={modalEvent}
          clients={clients}
          onSave={saveEvent}
          onDelete={deleteEvent}
          onClose={() => setModalEvent(null)}
        />
      )}
      {modalTask && (
        <TaskModal
          task={modalTask}
          clients={clients}
          onSave={saveTask}
          onDelete={deleteTask}
          onClose={() => setModalTask(null)}
        />
      )}

      <ConfirmDialog
        open={!!confirmDel}
        title={confirmDel?.type === 'event' ? 'Supprimer l\'événement ?' : 'Supprimer la tâche ?'}
        message={`"${confirmDel?.label}" sera définitivement supprimé${confirmDel?.type === 'event' ? ' (et retiré de Google Calendar si synchronisé)' : ''}.`}
        confirmLabel="Supprimer"
        danger
        onConfirm={() => confirmDel?.type === 'event' ? _doDeleteEvent(confirmDel.id) : _doDeleteTask(confirmDel.id)}
        onCancel={() => setConfirmDel(null)}
      />
    </div>
  );
}

// ─── Styles des boutons navigation ───────────────────────────────────────────
const navBtnStyle = {
  background: 'transparent', border: '1px solid #2a2d3e',
  color: '#94a3b8', cursor: 'pointer',
  borderRadius: 6, padding: '4px 10px', fontSize: 14,
};

const topBtnStyle = {
  background: 'transparent', border: '1px solid #2a2d3e',
  cursor: 'pointer', borderRadius: 6, padding: '7px 14px', fontSize: 13,
};
